import { fetchApi } from "./api"
import { mockDeviceService } from "@/mocks/mock-service"
import type { GapDeviceConfig, CreateGapDeviceRequest, UpdateGapDeviceRequest } from "@/types/gap-device"
import type { BulkOperationResult } from "@/types/api"

// Check if mock mode is enabled
const USE_MOCKS = import.meta.env.VITE_USE_MOCKS === "true"

// Real API service
const realDeviceService = {
  async getByClient(_clientId: string): Promise<GapDeviceConfig[]> {
    // Backend doesn't support filtering by client, so fetch all devices
    // The frontend will filter by clientId as needed
    // If backend adds query param support, we can use: `/api/v1/config/gap/devices?clientId=${clientId}`
    return fetchApi<GapDeviceConfig[]>("/api/v1/config/gap/devices")
  },

  async getById(deviceId: string): Promise<GapDeviceConfig> {
    return fetchApi<GapDeviceConfig>(`/api/v1/config/gap/devices/${deviceId}`)
  },

  async create(data: CreateGapDeviceRequest): Promise<GapDeviceConfig> {
    return fetchApi<GapDeviceConfig>("/api/v1/config/gap/devices", {
      method: "POST",
      body: JSON.stringify(data),
    })
  },

  async update(deviceId: string, data: UpdateGapDeviceRequest): Promise<GapDeviceConfig> {
    return fetchApi<GapDeviceConfig>(`/api/v1/config/gap/devices/${deviceId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  },

  async delete(deviceId: string): Promise<void> {
    return fetchApi<void>(`/api/v1/config/gap/devices/${deviceId}`, {
      method: "DELETE",
    })
  },
}

// Select service based on environment
const baseService = USE_MOCKS ? mockDeviceService : realDeviceService

export const deviceService = {
  ...baseService,

  async bulkUpdate(updates: Array<{ deviceId: string; data: UpdateGapDeviceRequest }>): Promise<BulkOperationResult> {
    const results = await Promise.allSettled(
      updates.map(({ deviceId, data }) => baseService.update(deviceId, data)),
    )

    const succeeded = results.filter((r) => r.status === "fulfilled").length
    const failed = results.filter((r) => r.status === "rejected").length
    const failedIds = updates.filter((_, i) => results[i].status === "rejected").map((u) => u.deviceId)

    return { succeeded, failed, failedIds }
  },

  async bulkDelete(deviceIds: string[]): Promise<BulkOperationResult> {
    const results = await Promise.allSettled(deviceIds.map((id) => baseService.delete(id)))

    const succeeded = results.filter((r) => r.status === "fulfilled").length
    const failed = results.filter((r) => r.status === "rejected").length
    const failedIds = deviceIds.filter((_, i) => results[i].status === "rejected")

    return { succeeded, failed, failedIds }
  },
}

// Log which mode is active (helpful for debugging)
if (import.meta.env.DEV) {
  console.log(`🔧 Device Service: ${USE_MOCKS ? "MOCK MODE" : "API MODE"}`)
}
